import { PageTitle } from "../../misc/PageTitle";
import { Row } from "../../misc/Row";
import { DrivingSchoolInstructors } from "./DrivingSchoolInstructors";
import { DrivingSchoolOfficeEmployees } from "./DrivingSchoolOfficeEmployees";
import { AddEmployeeButton } from "../../buttons/AddEmployeeButton";
import { currentUser, grants } from "../../backoffice.access_control";
import { Error, useGetList, useGetRecordId } from "react-admin";
import { Instructor } from "../../providers/instructorsProvider";
import { drivingSchoolEmployeeRoles, type Employee } from "../../providers/employeesProvider";
import intersection from "lodash/intersection";
import { ResponsiveHashTabs } from "../../misc/ResponsiveHashTabs";
import { Box, Skeleton, useMediaQuery } from "@mui/material";
import type { Theme } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useLocation } from "react-router";
import { EmployeesListSkeleton } from "../../lists/EmployeesListSkeleton";
import { ResponsiveListSurface } from "../../misc/ResponsiveListSurface";

export function DrivingSchoolEmployees() {
  const drivingSchoolId = useGetRecordId();
  const showOfficeTab = grants.has("Employee:list");
  const { hash } = useLocation();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const {
    data: inactiveInstructors,
    error: inactiveInstructorsError,
    refetch: refetchInactiveInstructors,
    isFetching: isFetchingInactiveInstructors,
  } = useGetList<Instructor>(
    "instructors",
    {
      filter: {
        drivingSchoolId,
        ...(grants.has("Employee:list") || grants.has("Instructor:list") ? {} : { id: currentUser?.uid }),
        isActive: false,
      },
      pagination: { page: 1, perPage: 1 },
    },
    { enabled: !!drivingSchoolId },
  );
  const {
    data: inactiveOtherEmployees,
    error: inactiveEmployeesError,
    refetch: refetchInactiveEmployees,
    isFetching: isFetchingInactiveEmployees,
  } = useGetList<Employee>(
    "employees",
    {
      filter: {
        drivingSchoolId,
        roles: (it: Array<Role>) =>
          intersection(drivingSchoolEmployeeRoles, it).length > 0 && !it.includes("instructor"),
        disabled: true,
      },
      pagination: { page: 1, perPage: 1 },
    },
    { enabled: !!drivingSchoolId && grants.has("Employee:list") },
  );

  const isLoadingTabs = !inactiveInstructors || (showOfficeTab && !inactiveOtherEmployees);
  const dependencyError =
    (!inactiveInstructors ? inactiveInstructorsError : undefined) ??
    (showOfficeTab && !inactiveOtherEmployees ? inactiveEmployeesError : undefined);
  const retryTabs = () => {
    void runSafely(
      () =>
        Promise.all([
          refetchInactiveInstructors({ throwOnError: true }),
          ...(showOfficeTab ? [refetchInactiveEmployees({ throwOnError: true })] : []),
        ]),
      { errorMessage: "Die Personalansichten konnten nicht erneut geladen werden." },
    );
  };
  const activeTabValue = hash.slice(1).split("&")[0];
  const loadingKind =
    showOfficeTab && ["office", "office-inactive", "support", "inactive-support"].includes(activeTabValue)
      ? "office"
      : "instructors";
  const showTabs = !!inactiveInstructors?.length || showOfficeTab;
  const tabs = [
    {
      value: "instructors",
      label: "Fahrlehrer",
      content: <DrivingSchoolInstructors surfaceVariant="compactNarrowTabContent" />,
    },
    ...(inactiveInstructors?.length
      ? [
          {
            value: "inactive-instructors",
            label: (
              <>
                Fahrlehrer
                <br />
                (inaktiv)
              </>
            ),
            mobileLabel: "Fahrlehrer (inaktiv)",
            content: <DrivingSchoolInstructors showInactive surfaceVariant="compactNarrowTabContent" />,
          },
        ]
      : []),
    ...(showOfficeTab
      ? [
          {
            value: "office",
            oldValue: "support",
            label: "Büro",
            content: <DrivingSchoolOfficeEmployees />,
          },
        ]
      : []),
    ...(inactiveOtherEmployees && inactiveOtherEmployees.length > 0
      ? [
          {
            value: "office-inactive",
            oldValue: "inactive-support",
            label: (
              <>
                Büro
                <br />
                (inaktiv)
              </>
            ),
            mobileLabel: "Büro (inaktiv)",
            content: <DrivingSchoolOfficeEmployees showInactive />,
          },
        ]
      : []),
  ];

  return (
    <div style={{ position: "relative" }}>
      <Row justifyContent="space-between" alignItems="center" sx={{ mb: "30px", minHeight: "32px" }}>
        <PageTitle documentTitle="Personal">
          <h2 style={{ margin: 0, lineHeight: 0.83 }}>Personal</h2>
        </PageTitle>
        {grants.has("Employee:create") && <AddEmployeeButton />}
      </Row>
      {isLoadingTabs ? (
        <>
          {!dependencyError && (showTabs || !inactiveInstructors) && (
            <Box
              aria-hidden="true"
              data-testid="EmployeesTabsSkeleton"
              sx={{
                display: "flex",
                overflow: "hidden",
                height: isMobile ? 40 : 50,
                mb: isMobile ? 1.5 : 0,
                "@media (prefers-reduced-motion: reduce)": {
                  "& .MuiSkeleton-root, & .MuiSkeleton-root::after": { animation: "none" },
                },
              }}
            >
              {Array.from({ length: isMobile ? 1 : tabs.length }, (_value, index) => (
                <Skeleton
                  key={index}
                  variant="rounded"
                  width={isMobile ? "100%" : 156}
                  height="100%"
                  sx={{ flexShrink: 0, borderRadius: isMobile ? "999px" : "16px 16px 0 0" }}
                />
              ))}
            </Box>
          )}
          <ResponsiveListSurface variant="compactNarrowTabContent" data-testid="DrivingSchoolEmployeesLoading">
            {dependencyError ? (
              <>
                <Error error={dependencyError} resetErrorBoundary={retryTabs} />
                <Row sx={{ justifyContent: "center", mt: 2 }}>
                  <LoadingButton
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    loading={isFetchingInactiveInstructors || (showOfficeTab && isFetchingInactiveEmployees)}
                    onClick={retryTabs}
                  >
                    Erneut laden
                  </LoadingButton>
                </Row>
              </>
            ) : (
              <EmployeesListSkeleton kind={loadingKind} variant="full" />
            )}
          </ResponsiveListSurface>
        </>
      ) : showTabs ? (
        <ResponsiveHashTabs tabs={tabs} mobileSelectAriaLabel="Personalansicht" />
      ) : (
        <DrivingSchoolInstructors surfaceVariant="compactNarrowStandalone" />
      )}
    </div>
  );
}
